/**
 * Auto-play: choose the next move for a goal, and run a sequence of moves
 * through the caller's move handler (the board's tap handler), so motion,
 * waves, streaks and scoring all run exactly as for a tap.
 */
import {
  detourOption,
  nextBestMove,
  remainingBestRoute,
  type BestMoveState,
} from "./bestMove";

export type AutoGoal = "step" | "oneBefore" | "win" | "detour";

export interface AutoState extends BestMoveState {
  status: string;
  graph: Record<string, { edges?: Record<string, number> } | undefined>;
}

export const AUTO_SPEEDS = { realistic: 700, fast: 150, instant: 0 } as const;
export type AutoSpeed = keyof typeof AUTO_SPEEDS;

/** Goals that make a single move. */
const SINGLE: ReadonlySet<AutoGoal> = new Set(["step", "detour"]);

export function nextAutoMove(s: AutoState, goal: AutoGoal): string | null {
  if (s.status !== "playing") return null;
  if (goal === "detour") return detourOption(s, s.graph);
  if (goal === "oneBefore" && remainingBestRoute(s).length - 1 <= 1) {
    return null;
  }
  return nextBestMove(s);
}

export interface RunOptions {
  goal: AutoGoal;
  getState: () => AutoState;
  /** The tap handler; the only way a move is made. */
  move: (word: string) => void;
  delayMs: number;
  wait?: (ms: number) => Promise<void>;
  cancelled?: () => boolean;
  /** Safety stop for a path that never ends. */
  maxMoves?: number;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Runs the goal; resolves with the number of moves made. Stops when the goal
 * is reached, on cancel, or when a move didn't take (the game is still on the
 * word it moved from after the wait), so a refused tap never loops.
 */
export async function runAutoPlay(o: RunOptions): Promise<number> {
  const wait = o.wait ?? sleep;
  const max = SINGLE.has(o.goal) ? 1 : (o.maxMoves ?? 60);
  let moves = 0;
  let movedFrom: string | null = null;
  while (moves < max && !o.cancelled?.()) {
    if (!nextAutoMove(o.getState(), o.goal)) break;
    if (moves > 0) {
      await wait(o.delayMs);
      if (o.cancelled?.()) break;
    }
    // Re-read: the game may have moved on (a tap) while we waited.
    const s = o.getState();
    if (moves > 0 && s.currentWord === movedFrom) break;
    const word = nextAutoMove(s, o.goal);
    if (!word) break;
    movedFrom = s.currentWord;
    o.move(word);
    moves++;
  }
  return moves;
}
